import { useEffect } from 'react';
import { api } from '@/lib/api';
import { mediaApiUrl, warmMedia } from '@/lib/mediaDiskCache';

function waitForRetry(signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const finish = () => { clearTimeout(timer); signal.removeEventListener('abort', finish); resolve(); };
    const timer = setTimeout(finish, 30_000);
    signal.addEventListener('abort', finish, { once: true });
    if (signal.aborted) finish();
  });
}

export function MediaWarmup({ municipalityId }: { municipalityId: string }) {
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    void (async () => {
      // Start immediately after municipality resolution, independently of the
      // current route. Retry while the API prepares missing derivatives.
      for (let attempt = 0; attempt < 20 && !signal.aborted; attempt++) {
        let pending = true;
        try {
          const tours = await api.virtualTours.list(municipalityId, { signal });
          const urls = [...tours.map((tour) => tour.optimizedPanoramaUrl),
            ...tours.map((tour) => tour.thumbnailUrl)].filter((url): url is string => Boolean(url));
          await warmMedia(urls.map(mediaApiUrl), signal);
          pending = tours.some((tour) => !tour.optimizedPanoramaUrl);
        } catch (error) {
          if (!signal.aborted) console.warn('[media-cache] Tour preload will retry', error);
        }
        // A failure preparing tours must not suppress the existing POI warmup.
        if (attempt === 0 && !signal.aborted) {
          try {
            const pois = await api.pois.list(municipalityId, { signal });
            const urls = pois.map((poi) => poi.thumbnailUrl).filter((url): url is string => Boolean(url));
            await warmMedia(urls.map(mediaApiUrl), signal);
          } catch { /* Foreground map loading remains available. */ }
        }
        if (!pending || signal.aborted || attempt === 19) break;
        await waitForRetry(signal);
      }
    })().catch((error) => {
      if (!signal.aborted) console.warn('[media-cache] Background preload unavailable', error);
    });
    return () => controller.abort();
  }, [municipalityId]);
  return null;
}
